/**
 * Placeholder data for the Plans and Billing screens.
 *
 * Estate Hub has **no subscription domain**: no plan, invoice, seat or MRR model
 * exists anywhere in the API. These screens are in the design comp, so they are
 * built here against typed fixtures — the shapes below are the contract those
 * screens expect, and swapping them for real queries should be the only change
 * needed once the backend lands.
 *
 * Every screen using this must show a `<PreviewNote>` saying so. Nothing here
 * should ever be presented as a real number.
 */

export interface Plan {
  id: string;
  name: string;
  /** Monthly price in rupees. */
  price: number;
  cadence: 'month' | 'year';
  blurb: string;
  seatLimit: number | null;
  propertyLimit: number | null;
  features: string[];
  agencies: number;
  featured?: boolean;
}

export const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 2999,
    cadence: 'month',
    blurb: 'A single-office agency finding its feet.',
    seatLimit: 5,
    propertyLimit: 150,
    agencies: 42,
    features: ['Properties, clients and visits', 'Deal tracking', 'Brokerage settings', 'Email support'],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 7999,
    cadence: 'month',
    blurb: 'The usual choice once there is a referral network.',
    seatLimit: 15,
    propertyLimit: 750,
    agencies: 61,
    featured: true,
    features: [
      'Everything in Starter',
      'Agent referral chains & splits',
      'Channel-partner projects',
      'Client occasion reminders',
      'Priority support',
    ],
  },
  {
    id: 'scale',
    name: 'Scale',
    price: 14999,
    cadence: 'month',
    blurb: 'Multi-branch agencies with a booking desk.',
    seatLimit: 30,
    propertyLimit: null,
    agencies: 33,
    features: [
      'Everything in Growth',
      'Unlimited properties',
      'Project bookings & developer master',
      'Reports and exports',
      'Dedicated onboarding',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0,
    cadence: 'month',
    blurb: 'Custom terms, negotiated per agency.',
    seatLimit: null,
    propertyLimit: null,
    agencies: 12,
    features: ['Everything in Scale', 'Unlimited seats', 'Custom brokerage rules', 'SLA & account manager'],
  },
];

export type InvoiceStatus = 'paid' | 'due' | 'overdue';

export interface Invoice {
  id: string;
  number: string;
  agency: string;
  plan: string;
  amount: number;
  issued: string;
  due: string;
  status: InvoiceStatus;
}

export const INVOICES: Invoice[] = [
  { id: '1', number: 'EH-2026-0148', agency: 'Horizon Realty', plan: 'Growth', amount: 7999, issued: '2026-07-01', due: '2026-07-14', status: 'paid' },
  { id: '2', number: 'EH-2026-0147', agency: 'Pinnacle Properties', plan: 'Scale', amount: 14999, issued: '2026-07-01', due: '2026-07-14', status: 'paid' },
  { id: '3', number: 'EH-2026-0146', agency: 'Nest & Loft', plan: 'Starter', amount: 2999, issued: '2026-07-01', due: '2026-07-14', status: 'paid' },
  { id: '4', number: 'EH-2026-0145', agency: 'Skyline Estates', plan: 'Growth', amount: 7999, issued: '2026-06-01', due: '2026-06-14', status: 'overdue' },
  { id: '5', number: 'EH-2026-0144', agency: 'Gateway Associates', plan: 'Growth', amount: 7999, issued: '2026-07-08', due: '2026-07-22', status: 'due' },
  { id: '6', number: 'EH-2026-0143', agency: 'Urban Edge Realty', plan: 'Growth', amount: 7999, issued: '2026-07-08', due: '2026-07-22', status: 'due' },
  { id: '7', number: 'EH-2026-0142', agency: 'Meridian Homes', plan: 'Scale', amount: 14999, issued: '2026-06-01', due: '2026-06-14', status: 'overdue' },
];

/** Monthly recurring revenue over the last year, in rupees. */
export const MRR_SERIES: { month: string; value: number }[] = [
  { month: 'Aug', value: 412000 },
  { month: 'Sep', value: 448000 },
  { month: 'Oct', value: 467000 },
  { month: 'Nov', value: 503000 },
  { month: 'Dec', value: 528000 },
  { month: 'Jan', value: 566000 },
  { month: 'Feb', value: 604000 },
  { month: 'Mar', value: 651000 },
  { month: 'Apr', value: 689000 },
  { month: 'May', value: 724000 },
  { month: 'Jun', value: 782000 },
  { month: 'Jul', value: 842000 },
];
